import React, { useContext, useEffect, useRef, useState } from "react";
import { StyleSheet, View } from "react-native";
import { Modal, Text, TouchableOpacity } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import Spinner from "../../../components/shared/Spinner";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useDispatch, useSelector } from "react-redux";
import {
  getTrackerEnquiriesByKey,
  getTrackerFlagByKey,
  getTrackerInterruptsByKey,
  searchFactoryEnquiriesByKey,
  searchFactoryInterruptsByKey,
  searchTrackerEnquiriesByKey,
} from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { LabelBannerWithSearchBar } from "../../../components/custom/Banners/LabelBannerWithSearchBar";
import { resetEnquiries } from "../../../redux/reducers/chinaTrackerReducer";
import ChinaTrackerEnquiryReply from "./ChinaTrackerEnquiryReply";
import {
  translateHeaders,
  useTranslation,
} from "../../../utils/useTranslation";
import { chinaTrackerTranslationMap } from "../../../utils/translations";
import TrackerHistoryReplyModal from "./TrackerHistoryReplyModal";
import { ChinaTrackerDataTableWithPagination2 } from "../../../components/custom/DataTable/tracker/ChinaTrackerDataTableWithPagination2";

interface TrackerEnquiryModalProps {
  visible: boolean;
  onClose: any;
  order: string;
}

const TrackerHistoryModal: React.FC<TrackerEnquiryModalProps> = ({
  visible,
  onClose,
  order,
}: any) => {
  const listRef = useRef<ScrollView>(null);

  const [isSearched, setIsSearched] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);

  const dispatch = useDispatch<any>();
  const {
    loadingEnquiries,
    enquiries,
    loadingFlag,
    factoryFlags,
    loadingInterrupts,
    interrupts,
  } = useSelector((state: any) => state.chinaTracker);
  const [activeTab, setActiveTab] = useState("enquiries"); // enquiries or history
  const [isRefresh, setRefresh] = useState<boolean>(false);

  useEffect(() => {
    if (!isRefresh) {
      const action =
        activeTab === "enquiries"
          ? getTrackerEnquiriesByKey(order.Key)
          : activeTab === "interrupts"
          ? getTrackerInterruptsByKey(order.Key)
          : getTrackerFlagByKey(order.Key);
      dispatch(action);
    }
  }, [order, activeTab]);

  useEffect(() => {
    if (isRefresh) {
      const action =
        activeTab === "enquiries"
          ? getTrackerEnquiriesByKey(order.Key)
          : activeTab === "interrupts"
          ? getTrackerInterruptsByKey(order.Key)
          : getTrackerFlagByKey(order.Key);
      dispatch(action);
      setRefresh(false);
    }
  }, [isRefresh]);

  const headers = {
    createdAt: {
      name: "Inquiry Date",
      width: 0.4,
    },
    createdBy: {
      name: "User",
      width: 0.3,
    },
    subject: {
      name: "Subject",
      width: 0.2,
    },
    priority: {
      name: "Priority",
      width: 0.2,
    },
    message: {
      name: "Message",
      width: 0.5,
    },
    enquiryActions: {
      name: "",
      width: 0.2,
      textAlign: "right",
    },
  };

  const factoryHeaders = {
    createdAt: {
      name: "Inquiry Date",
      width: 0.4,
    },
    createdByName: {
      name: "User",
      width: 0.3,
    },
    subject: {
      name: "Subject",
      width: 0.2,
    },
    resolvedDate: {
      name: "Resolved Date",
      width: 0.2,
    },
    message: {
      name: "Message",
      width: 0.5,
    },
  };

  const interruptHeaders = {
    runOrder: {
      name: "Run Order",
      width: 0.1,
    },
    message: {
      name: "Reason",
      width: 1.0,
    },
    interruptType: {
      name: "Interrupt Type",
      width: 0.2,
    },
    createdAt: {
      name: "Interrupted At",
      width: 0.4,
    },
    createdByName: {
      name: "Interrupted By",
      width: 0.3,
    },
  };

  const [tableHeaders, setTableHeaders] = useState<any>(headers);

  const { isLargeDevice, isMediumDevice, isSmallDevice } = useContext(
    DeviceOrientationContext
  );
  const [, setContentOffSet] = useState<number>(0);
  const { t } = useTranslation();

  const handleScroll = (event: any) => {
    setContentOffSet(Math.floor(event.nativeEvent?.contentOffset?.y) ?? 0);
  };

  useEffect(() => {
    let isMounted: boolean = true;

    setTableHeaders(
      translateHeaders(
        activeTab === "enquiries"
          ? headers
          : activeTab === "interrupts"
          ? interruptHeaders
          : factoryHeaders,
        t,
        chinaTrackerTranslationMap
      )
    );

    return () => {
      isMounted = false;
    };
  }, [activeTab, isLargeDevice]);

  const handleSearch = (searchedText: string) => {
    setPage(0);
    if (searchedText) {
      const action =
        activeTab === "enquiries"
          ? searchTrackerEnquiriesByKey({
              key: order.Key,
              search: searchedText,
            })
          : activeTab === "interrupts"
          ? searchFactoryInterruptsByKey({
              key: order.Key,
              search: searchedText,
            })
          : searchFactoryEnquiriesByKey({
              key: order.Key,
              search: searchedText,
            });
      dispatch(action);
    } else {
      dispatch(resetEnquiries());
    }
  };

  const [modalOpen, setModalOpen] = useState<boolean>();

  const onCloseEnquiry = () => {
    setModalOpen(false);
  };
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedEnquiryType, setSelectedEnquiryType] = useState<any>(null);
  const navigateToOrder = (order: any, action: string) => {
    if (activeTab === "enquiries") {
      setModalOpen(true);
      setSelectedItem(order);
      setSelectedEnquiryType(action);
    }
  };

  return (
    <>
      {modalOpen && selectedEnquiryType === "reply" && (
        <ChinaTrackerEnquiryReply
          onClose={onCloseEnquiry}
          visible={modalOpen}
          order={selectedItem}
          rootOrder={order}
        />
      )}

      {modalOpen && selectedEnquiryType != "reply" && (
        <TrackerHistoryReplyModal
          onClose={onCloseEnquiry}
          visible={modalOpen}
          order={selectedItem}
        />
      )}
      <Modal visible={visible} transparent animationType="slide">
        <View style={styles.overlay}>
          <View
            style={
              isLargeDevice
                ? styles.modalContainer
                : styles.modalContainerSecond
            }
          >
            <Text style={styles.title}>{t("history")}</Text>

            <ScrollView onScroll={handleScroll} ref={listRef}>
              <View>
                <View>
                  <View
                    style={[
                      styles.containerContent,
                      {
                        width: isLargeDevice
                          ? "100%"
                          : isMediumDevice
                          ? "100%"
                          : "100%",
                      },
                    ]}
                  >
                    {!loadingEnquiries && !loadingFlag && !loadingInterrupts ? (
                      <View style={{ padding: 2 }}>
                        <LabelBannerWithSearchBar
                          titleName={
                            t("totalItems") +
                            `: ${
                              activeTab === "enquiries"
                                ? enquiries.length
                                : activeTab === "interrupts"
                                ? interrupts.length
                                : factoryFlags.length
                            }`
                          }
                          handleSearch={handleSearch}
                          setRefresh={setRefresh}
                          dataToExport={
                            activeTab === "enquiries"
                              ? enquiries
                              : activeTab === "interrupts"
                              ? interrupts
                              : factoryFlags
                          }
                          fileName="ItemTrackerHistoryScreen"
                          sheetName="TrackerHistoryScreen"
                        />

                        <View style={styles.tabsContainer}>
                          <TouchableOpacity
                            style={[
                              styles.tab,
                              activeTab === "enquiries" && styles.activeTab,
                            ]}
                            onPress={() => setActiveTab("enquiries")}
                          >
                            <Text
                              style={[
                                styles.tabText,
                                activeTab === "enquiries" &&
                                  styles.activeTabText,
                              ]}
                            >
                              {t("enquiries")}
                            </Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={[
                              styles.tab,
                              activeTab === "flagged" && styles.activeTab,
                            ]}
                            onPress={() => setActiveTab("flagged")}
                          >
                            <Text
                              style={[
                                styles.tabText,
                                activeTab === "flagged" && styles.activeTabText,
                              ]}
                            >
                              {t("flagged")}
                            </Text>
                          </TouchableOpacity>

                          <TouchableOpacity
                            style={[
                              styles.tab,
                              activeTab === "interrupts" && styles.activeTab,
                            ]}
                            onPress={() => setActiveTab("interrupts")}
                          >
                            <Text
                              style={[
                                styles.tabText,
                                activeTab === "interrupts" &&
                                  styles.activeTabText,
                              ]}
                            >
                              {t("interrupts")}
                            </Text>
                          </TouchableOpacity>
                        </View>

                        <ChinaTrackerDataTableWithPagination2
                          headers={tableHeaders}
                          rows={
                            activeTab === "enquiries"
                              ? enquiries
                              : activeTab === "interrupts"
                              ? interrupts
                              : factoryFlags
                          }
                          itemsPerPage={10}
                          isDataLoading={isSearched}
                          isDisplayTimeWithDate={true}
                          useModal={true}
                          isDetail={false}
                          isEdit={false}
                          isDelete={false}
                          // Required props
                          setModalVisible={() => {}}
                          handleRowClick={() => {}}
                          setSelected={() => {}}
                          handleDeleteRow={() => {}}
                          operationActions={navigateToOrder}
                          page={page}
                          setPage={setPage}
                        />
                      </View>
                    ) : (
                      <View>
                        <Spinner
                          size="small"
                          color="#74BBFF"
                          styles={{ flexDirection: "row", margin: "20%" }}
                        />
                      </View>
                    )}
                  </View>
                </View>
              </View>
            </ScrollView>
            <View style={styles.footer}>
              <TouchableOpacity onPress={onClose} style={styles.closeButton}>
                <Text>{t("close")}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.2)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContainer: {
    backgroundColor: "#fff",
    padding: 25,
    width: "76%",
    borderRadius: 10,
  },
  title: {
    fontSize: 25,
    fontWeight: "bold",
    marginBottom: 35,
  },
  detailsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 15,
  },
  column: {
    flex: 1,
  },
  label: {
    fontWeight: "bold",
    fontSize: 16,
    marginBottom: 3,
  },
  value: {
    fontSize: 16,
    marginBottom: 10,
    paddingLeft: 4,
  },
  dropdownRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  dropdownContainer: {
    flex: 1,
    marginRight: 10,
  },
  inputLabel: {
    fontWeight: "bold",
    marginBottom: 5,
    marginTop: 20,
  },
  picker: {
    borderWidth: 1.5,
    borderColor: "#a6a6a6",
    height: 40,
    backgroundColor: "#fff",
    borderRadius: 10,
  },
  messageInput: {
    height: 100,
    borderWidth: 1.5,
    borderColor: "#a6a6a6",
    padding: 10,
    backgroundColor: "#fff",
    marginBottom: 15,
    borderRadius: 10,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  userName: {
    fontSize: 14,
  },
  submitButton: {
    backgroundColor: "#000",
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 5,
  },
  submitText: {
    color: "#fff",
    fontWeight: "bold",
  },
  closeButton: {
    padding: 10,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
  },
  buttonPrimary: { padding: 10, backgroundColor: "black", borderRadius: 5 },
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    flexGrow: 1,
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
  },

  fab: {
    bottom: -10,
    position: "absolute",
    zIndex: 1,
  },
  tabsContainer: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    marginBottom: 10,
  },
  tab: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },
  activeTab: {
    borderBottomColor: "#3498db",
  },
  tabText: {
    color: "#999",
  },
  activeTabText: {
    color: "#3498db",
    fontWeight: "600",
  },
  modalContainerSecond: {
    backgroundColor: "#fff",
    elevation: 8,
    height: "100%",
    width: "100%",
    padding: 10,
  },
});

export default TrackerHistoryModal;
